import { Application } from '@bybeam/platform-types';
import usePartnerPrograms from '@platform-ui-common/hooks/contexts/PartnerPrograms/index.js';
import useAuth from '@platform-ui/auth/hooks/useAuth/index.js';
import { Card, DataList, Flex, Heading, Spinner, Table } from '@radix-ui/themes';
import { useMemo } from 'react';
import styles from './ApplicationTable.module.css';
import { getColumns, prepData } from './columns.js';

export interface ApplicationSummaryTableProps {
  applications: Application[];
}

export default function ApplicationSummaryTable({
  applications,
}: ApplicationSummaryTableProps): JSX.Element {
  const { programs = [], loading } = usePartnerPrograms();
  const { user } = useAuth();

  const coreUser = user?.core;
  const isLoading = loading || !coreUser;

  const preppedData = useMemo(
    () => (coreUser && programs?.length > 0 ? prepData(programs, applications, coreUser) : []),
    [programs, applications, coreUser],
  );
  const columns = useMemo(
    () => (coreUser && programs?.length > 0 ? getColumns(programs, coreUser) : []),
    [programs, coreUser],
  );

  return (
    <div className="w-full bg-grayBackground">
      <Flex direction="column" mx="4" py="4" gap="4" align="start">
        <Heading weight="medium" size="5">
          Your Applications
        </Heading>
        {isLoading ? (
          <Flex direction="row" gap="1" justify="start" align="center">
            <div>Loading...</div>
            <Spinner size="3" />
          </Flex>
        ) : (
          <>
            <div className={styles.desktopView}>
              <Table.Root variant="surface">
                <Table.Header>
                  <Table.Row>
                    {columns.map((column) => (
                      <Table.ColumnHeaderCell key={column.accessor}>
                        {column.Header as string}
                      </Table.ColumnHeaderCell>
                    ))}
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {preppedData.map((row) => (
                    <Table.Row key={row.id}>
                      {columns.map(({ Cell, accessor }) => (
                        <Table.Cell key={accessor}>
                          {Cell ? <Cell value={row[accessor]} /> : row[accessor]}
                        </Table.Cell>
                      ))}
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table.Root>
            </div>
            <div className={styles.mobileView}>
              <Flex direction="column" gap="2">
                {preppedData.map((row) => (
                  <Card key={row.id}>
                    <DataList.Root>
                      {columns.map(({ Cell, accessor, Header }) => (
                        <DataList.Item key={Header as string} align="start">
                          <DataList.Label minWidth="130px">{Header as string}</DataList.Label>
                          <DataList.Value>
                            {Cell ? <Cell value={row[accessor]} /> : row[accessor]}
                          </DataList.Value>
                        </DataList.Item>
                      ))}
                    </DataList.Root>
                  </Card>
                ))}
              </Flex>
            </div>
          </>
        )}
      </Flex>
    </div>
  );
}
